import ResizeHandle from 'components/common/ResizeHandle';
import {
  INPUT_DOC_LENS_NUTRITION,
  INPUT_DOC_TAB_DIAGNOSTICS,
  INPUT_DOC_TAB_INSIGHTS,
  RIGHT_PANEL_CANONICAL_ORDER,
  RIGHT_PANEL_INITIAL_WIDTH,
  RIGHT_PANEL_MAX_WIDTH,
  RIGHT_PANEL_MIN_WIDTH,
  RIGHT_PANEL_STATE_STORAGE_KEY,
  RIGHT_PANEL_TOOL_LIME_MATE,
  RIGHT_PANEL_WIDTH_STORAGE_KEY,
  SIDEBAR_COLLAPSED_STORAGE_KEY,
  SIDEBAR_INFO_BANNER_STORAGE_KEY,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_WIDTH_STORAGE_KEY,
  SUMMARY_SCOPE_HINT_STORAGE_KEY,
} from 'constants/uiConstants';
import {
  CROP_KEY_PREFIX,
  FARM_KEY_PREFIX,
  INPUT_KEY_PREFIX,
  ORG_KEY_PREFIX,
  PADDOCK_KEY_PREFIX,
} from 'constants/routeConstants';
import { deriveScopeLevel } from 'pages/FarmsPage/Workspace/Insights/insightUtils';

// Space consumed by the edge strip (w-6) and the two 0.25rem drag handles.
// These are flex-shrink-0 and must always be visible — the pinned panel is
// clamped so that sidebar-min + workspace-0 + these reserves fit the viewport.
const EDGE_STRIP_REM = 1.5;
const DRAG_HANDLES_REM = 0.5;
import { useResizablePanel } from 'hooks/common/useResizablePanel';
import React, {
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/Store';
import {
  bringRightPanelTabToFront,
  collapseAllRightPanelTabs,
  resetAllRightPanelTabs,
  collapseRightPanelTab,
  enterAnalysisCollapse,
  FloatingGeometry,
  floatRightPanelTab,
  leaveAnalysisCollapse,
  pinAllRightPanelTabs,
  pinRightPanelTab,
  RightPanelTabId,
  setRightPanelFloatingGeometry,
  setRightPanelActiveTool,
  setRightPanelMerged,
  setSummaryHintVisible,
} from '../../redux/slices/InsightsSlice';
import {
  resetSidebarWidth,
  setSidebarCollapsed,
  setSidebarInfoBannerVisible,
} from '../../redux/slices/FarmSlice';
import { Tooltip } from 'antd';
import { IoIosArrowDropdownCircle } from 'react-icons/io';
import RightEdgeLabels from './RightEdgeLabels';
import RightFloatingPanel from './RightFloatingPanel';
import RightPinnedPanel from './RightPinnedPanel';

/**
 * VS-style collapsible right panel shell.
 *
 * Renders three regions within the FarmsPage flex row:
 *  1. Drag handle + fixed-width RightPinnedPanel (only when ≥1 tab is pinned)
 *  2. RightEdgeLabels strip (vertical labels for non-pinned tabs, always visible
 *     while any tab is not pinned)
 *  3. RightFloatingPanel overlay (react-rnd, when a tab is floating)
 *
 * Content slots remain placeholders until OP-2274.
 */
interface RightPanelShellProps {
  /** Current left sidebar width in rem (0 when collapsed). Used to clamp the
   *  pinned panel so the edge strip is never pushed off screen. */
  sidebarWidth: number;
  /** DOM ref for the sidebar element. Used only as a ResizeObserver trigger —
   *  fires clampPanelToViewport in real time during left-sidebar drag. */
  sidebarRef?: RefObject<HTMLDivElement>;
  /** Width ref from useResizableSidebar. Holds the intended (drag-target)
   *  sidebar width in rem — not the flex-compressed rendered width that
   *  getBoundingClientRect() would return when the panel is already wide. */
  sidebarWidthRef?: MutableRefObject<number>;
  /** Ref for the FarmsPage flex container. Its measured width is the true
   *  available space for all panels — narrower than window.innerWidth when a
   *  fixed-position element (e.g. LeftNavRail) has reserved left-edge space
   *  via padding on the parent container. */
  containerRef?: RefObject<HTMLDivElement>;
  /** Ref into useResizableSidebar's effective-max ceiling. Updated by
   *  clampPanelToViewport so the sidebar drag is blocked once it would push
   *  the pinned panel + edge strip below their minimum combined width. */
  sidebarMaxRef?: MutableRefObject<number>;
  /** React state setter from useResizableSidebar. Called by clampPanelToViewport
   *  when it directly corrects the sidebar DOM width so React state stays in
   *  sync and the next render does not undo the manual DOM correction. */
  setSidebarWidth?: (width: number) => void;
}

const RightPanelShell: React.FC<RightPanelShellProps> = ({
  sidebarWidth,
  sidebarRef,
  sidebarWidthRef,
  containerRef,
  sidebarMaxRef,
  setSidebarWidth,
}) => {
  const dispatch = useDispatch();
  const rightPanel = useSelector(
    (state: RootState) => state.insightsReducer.rightPanel
  );
  const {
    tabs,
    tabOrder,
    floatingTabIds,
    activePinnedTabId,
    isMerged,
    mergedSectionExpanded,
    floatingGeometry,
    activeTool,
  } = rightPanel;

  // ── Node selection — used to detect workspace layout transitions ─────────
  // Only the transition from "no input" → "input selected" (or back) changes
  // the workspace's min-content width; switching from one input to another
  // leaves the workspace layout identical. Using a boolean avoids re-clamping
  // on every input-to-input hop, which was causing the panel to shrink to its
  // minimum width on each selection change.
  const hasSelectedInput = useSelector(
    (state: RootState) => state.inputReducer.selectedInputId !== null
  );

  // ── Scope gate (OP-2333) ────────────────────────────────────────────────
  // The Live Panel only makes sense once a specific paddock or plan is in
  // scope — at org/farm scope it's forced fully collapsed and inert, with a
  // tooltip explaining why on any attempted interaction. This overrides the
  // real `tabs` status for rendering purposes only; the underlying Redux
  // state is untouched, so whatever was pinned/floating reappears exactly as
  // it was the moment scope becomes applicable again.
  const activeTreeNodeKey = useSelector(
    (state: RootState) => state.farmReducer.activeTreeNodeKey
  );
  const scopeLevel = deriveScopeLevel(activeTreeNodeKey, {
    input: INPUT_KEY_PREFIX,
    paddock: PADDOCK_KEY_PREFIX,
    farm: FARM_KEY_PREFIX,
    crop: CROP_KEY_PREFIX,
    org: ORG_KEY_PREFIX,
  });
  const isScopeApplicable = scopeLevel === 'paddock' || scopeLevel === 'input';

  // ── Insights / Diagnostics auto-collapse (OP-2333, OP-2390) ─────────────
  // Insights and Diagnostics already surface the same cost/rate figures as
  // the Live Panel, so entering either force-collapses the panel (but, unlike
  // the scope gate above, leaves it fully interactive — the user can still
  // manually re-pin a tab). Returning to Planning restores the pre-collapse
  // layout unless the user changed something while away. Moving between
  // Insights and Diagnostics keeps whatever the user did. See
  // enterAnalysisCollapse / leaveAnalysisCollapse in InsightsSlice for the
  // snapshot/restore logic.
  const activeTab = useSelector(
    (state: RootState) => state.insightsReducer.activeTab
  );
  const activeLens = useSelector(
    (state: RootState) => state.insightsReducer.activeLens
  );
  const isCollapsedViewActive =
    activeTab === INPUT_DOC_TAB_INSIGHTS ||
    activeTab === INPUT_DOC_TAB_DIAGNOSTICS;
  const wasCollapsedViewActiveRef = useRef(isCollapsedViewActive);
  useEffect(() => {
    if (isCollapsedViewActive && !wasCollapsedViewActiveRef.current) {
      dispatch(enterAnalysisCollapse());
    } else if (!isCollapsedViewActive && wasCollapsedViewActiveRef.current) {
      dispatch(leaveAnalysisCollapse());
    }
    wasCollapsedViewActiveRef.current = isCollapsedViewActive;
  }, [isCollapsedViewActive, dispatch]);

  const pinnedTabIds = isScopeApplicable
    ? tabOrder.filter((id) => tabs[id] === 'pinned')
    : [];
  const edgeTabIds = RIGHT_PANEL_CANONICAL_ORDER.filter(
    (id) => !isScopeApplicable || tabs[id] !== 'pinned'
  ) as RightPanelTabId[];
  // LimeMate (OP-2383) keeps the pinned panel open on its own, even with no
  // live totals tab pinned. It follows the same scope gate as the tabs.
  const isLimeMateOpen =
    isScopeApplicable && activeTool === RIGHT_PANEL_TOOL_LIME_MATE;
  const hasPinnedTabs = pinnedTabIds.length > 0 || isLimeMateOpen;

  // ── Pinned panel resize ─────────────────────────────────────────────────
  // maxPanelWidthRef tracks the current effective max — updated by
  // clampPanelToViewport so the drag handler itself can never exceed it.
  const maxPanelWidthRef = useRef(RIGHT_PANEL_MAX_WIDTH);
  // Tracks a pending rAF for deferred sidebar DOM correction. Cancelled when a
  // newer correction supersedes it so only the latest value is committed.
  const sidebarCorrectionRafRef = useRef<number | null>(null);
  // Debounce timer for releasing the sidebar constraint. The constraint must
  // not be released immediately on "no overflow" because during an active
  // right-drag the sidebar is clamped at the boundary — "no overflow" is
  // transient (next onMouseMove re-overflows) and immediate release causes
  // oscillation. The timer only fires when no overflow has been seen for
  // 200 ms, meaning the drag is truly idle.
  const releaseConstraintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { panelRef, width, widthRef, setWidth, onMouseDown } =
    useResizablePanel({
      min: RIGHT_PANEL_MIN_WIDTH,
      max: RIGHT_PANEL_MAX_WIDTH,
      maxRef: maxPanelWidthRef,
      initial: RIGHT_PANEL_INITIAL_WIDTH,
      // When all tabs are pinned, edgeTabIds is empty and the edge-strip
      // placeholder div is not rendered in the flex row at all — the panel
      // may legitimately extend all the way to window.innerWidth. Passing a
      // fixed EDGE_STRIP_REM regardless would make the hook's own reactive
      // overflow guard believe the panel has overflowed well before it
      // actually has, repeatedly correcting it down to the same artificially
      // tight width — felt as the drag "jumping to increments" instead of
      // tracking the cursor.
      rightEdgePadRem: edgeTabIds.length > 0 ? EDGE_STRIP_REM : 0,
    });

  useEffect(() => {
    const saved = localStorage.getItem(RIGHT_PANEL_WIDTH_STORAGE_KEY);
    if (saved) {
      const value = Number(saved);
      if (value >= RIGHT_PANEL_MIN_WIDTH && value <= RIGHT_PANEL_MAX_WIDTH) {
        widthRef.current = value;
        setWidth(value);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(RIGHT_PANEL_WIDTH_STORAGE_KEY, width.toString());
  }, [width]);

  // ── Persist rightPanel Redux state (excluding floatingTabId) ────────────
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      const {
        tabs: t,
        tabOrder: o,
        activePinnedTabId: a,
        isMerged: m,
        mergedSectionExpanded: e,
        floatingGeometry: g,
        chartVisible: cv,
        categoryFilter: cf,
        activeTool: at,
      } = rightPanel;
      localStorage.setItem(
        RIGHT_PANEL_STATE_STORAGE_KEY,
        JSON.stringify({
          tabs: t,
          tabOrder: o,
          activePinnedTabId: a,
          isMerged: m,
          mergedSectionExpanded: e,
          floatingGeometry: g,
          chartVisible: cv,
          categoryFilter: cf,
          activeTool: at,
        })
      );
    }, 300);
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [rightPanel]);

  // ── Re-assert panel width after every render ────────────────────────────
  useLayoutEffect(() => {
    if (panelRef.current) {
      panelRef.current.style.width = `${widthRef.current}rem`;
    }
  });

  // ── Clamp pinned panel width to keep the edge strip always on screen ──────
  // Guarantees: actualSidebar + workspace + rightPanel + edgeStrip ≤ viewport.
  // Uses the real sidebar width (not the min constant) so that dragging the
  // left sidebar wider correctly shrinks the pinned panel before the edge
  // strip is pushed off screen. Re-runs whenever sidebarWidth changes because
  // the useCallback dep change causes the window-resize useEffect to re-fire.
  const clampPanelToViewport = useCallback(() => {
    const fs = parseFloat(getComputedStyle(document.documentElement).fontSize);
    // sidebarWidthRef.current reflects the actual rendered sidebar width:
    // useResizableSidebar's RAF detects flex compression (workspace at min-content
    // preventing the sidebar from reaching its drag target) and updates widthRef
    // to the rendered value, so this is always an accurate measure of the space
    // the sidebar truly occupies.
    const intendedSidebarRem = sidebarWidthRef?.current ?? sidebarWidth;
    // Use the FarmsPage container width rather than window.innerWidth.
    // The LeftNavRail is position:fixed and the parent <main> has md:pl-16
    // (64px), so the container is narrower than the full viewport. Using
    // window.innerWidth here over-allocates space by the nav-rail width,
    // letting the panel grow wide enough to push the edge strip off screen.
    const containerWidthPx =
      containerRef?.current?.getBoundingClientRect().width ?? window.innerWidth;
    const vwRem = containerWidthPx / fs;

    // When all tabs are pinned, edgeTabIds is empty and the edge-strip
    // placeholder div is not rendered in the flex row — the panel may
    // legitimately extend all the way to window.innerWidth. Only reserve the
    // strip's width when it is actually present in the layout.
    const effectiveEdgeRem = edgeTabIds.length > 0 ? EDGE_STRIP_REM : 0;

    const maxAllowed = Math.max(
      RIGHT_PANEL_MIN_WIDTH,
      vwRem - intendedSidebarRem - effectiveEdgeRem - DRAG_HANDLES_REM
    );
    // Always update the drag-time ceiling so onMouseMove can never exceed it.
    maxPanelWidthRef.current = Math.min(RIGHT_PANEL_MAX_WIDTH, maxAllowed);

    // When there is no pinned panel there is nothing to protect — release any
    // previous sidebar constraint so the user can freely resize the sidebar.
    if (sidebarMaxRef && !hasPinnedTabs) {
      sidebarMaxRef.current = SIDEBAR_MAX_WIDTH;
    }

    if (!panelRef.current) return;
    if (widthRef.current > maxAllowed) {
      widthRef.current = maxAllowed;
      panelRef.current.style.width = `${maxAllowed}rem`;
      setWidth(maxAllowed);
    }

    // DOM overflow guard: the geometric formula above cannot account for the
    // workspace's min-content width. When the workspace resists further
    // compression (data loaded), the right panel's right edge can overflow the
    // intended boundary even after the geometric clamp. Apply the intended width
    // and force a synchronous reflow so we can read the panel's actual position.
    // If the panel overflows: shrink it to the corrected size (minimum
    // RIGHT_PANEL_MIN_WIDTH). We intentionally do NOT auto-collapse here:
    // collapsing would undo user actions like "Pin All" when the workspace
    // min-content is just slightly wider than the available space.
    panelRef.current.style.width = `${widthRef.current}rem`;
    const panelRect = panelRef.current.getBoundingClientRect();
    const intendedRight = window.innerWidth - effectiveEdgeRem * fs;
    if (panelRect.right > intendedRight + 1) {
      const overflowRem = (panelRect.right - intendedRight) / fs;
      // Capture the pre-correction panel width so we can precisely compute
      // how much of the overflow the panel absorbed vs. what the sidebar caused.
      const panelWidthBeforeCorrection = widthRef.current;
      const correctedRem = Math.max(
        RIGHT_PANEL_MIN_WIDTH,
        panelWidthBeforeCorrection - overflowRem
      );
      widthRef.current = correctedRem;
      panelRef.current.style.width = `${correctedRem}rem`;
      setWidth(correctedRem);
      // Do NOT update maxPanelWidthRef here. The geometric ceiling computed
      // above already reflects available space; the DOM overflow correction is
      // a one-time stored-width adjustment. Tightening maxPanelWidthRef to
      // correctedRem (especially when it was floored to RIGHT_PANEL_MIN_WIDTH)
      // would lock out drag entirely. useResizablePanel's own per-frame
      // overflow guard handles corrections dynamically during active drag.

      // ── Sidebar constraint (DOM-derived, precise) ─────────────────────────
      // Cancel any pending constraint release: overflow means the constraint
      // is still actively needed — don't let the debounce timer clear it.
      if (releaseConstraintTimerRef.current) {
        clearTimeout(releaseConstraintTimerRef.current);
        releaseConstraintTimerRef.current = null;
      }
      // When the panel cannot absorb all the overflow (it has hit its minimum),
      // the sidebar is responsible for the remaining excess. Compute exactly
      // how much of the overflow the panel could NOT absorb, then subtract that
      // from the current sidebar width to get the precise safe ceiling.
      // This prevents the ceiling from being set too high (which was the root
      // cause of the imprecision: intendedSidebarRem alone is already the
      // overflow-causing position, so using it as the ceiling is no constraint
      // at all).
      if (sidebarMaxRef && edgeTabIds.length > 0) {
        if (correctedRem === RIGHT_PANEL_MIN_WIDTH) {
          const panelAbsorbed = Math.max(
            0,
            panelWidthBeforeCorrection - RIGHT_PANEL_MIN_WIDTH
          );
          const sidebarExcessRem = overflowRem - panelAbsorbed;
          const safeSidebarRem = Math.min(
            SIDEBAR_MAX_WIDTH,
            Math.max(SIDEBAR_MIN_WIDTH, intendedSidebarRem - sidebarExcessRem)
          );
          sidebarMaxRef.current = safeSidebarRem;
          if (sidebarWidthRef && safeSidebarRem < intendedSidebarRem) {
            // Update the ref synchronously so onMouseUp commits the correct
            // value even if the rAF below hasn't fired yet.
            sidebarWidthRef.current = safeSidebarRem;
            // Defer DOM + React state changes to the next animation frame.
            // Making a synchronous DOM size change inside a ResizeObserver
            // callback (which is observing that same element) triggers the
            // "ResizeObserver loop completed with undelivered notifications"
            // browser error. Moving the mutation outside the delivery cycle
            // via rAF eliminates the error. Cancel any pending correction so
            // only the latest safeSidebarRem value is applied.
            if (sidebarCorrectionRafRef.current !== null) {
              cancelAnimationFrame(sidebarCorrectionRafRef.current);
            }
            sidebarCorrectionRafRef.current = requestAnimationFrame(() => {
              sidebarCorrectionRafRef.current = null;
              if (sidebarRef?.current) {
                sidebarRef.current.style.width = `${safeSidebarRem}rem`;
              }
              setSidebarWidth?.(safeSidebarRem);
            });
          }
        } else {
          // Panel fully absorbed the overflow — sidebar is within safe bounds.
          sidebarMaxRef.current = SIDEBAR_MAX_WIDTH;
        }
      }
    } else {
      // No overflow — schedule a debounced release of the sidebar constraint.
      // Releasing immediately causes oscillation: the sidebar is clamped at
      // sidebarMaxRef.current by onMouseMove (user is still dragging right),
      // the clamped position no longer overflows so we'd release, then the
      // next onMouseMove pushes the sidebar back over the limit and we apply
      // the constraint again — an endless flicker loop.
      // By deferring 200 ms, any overflow event from the ongoing drag cancels
      // the timer, and the constraint persists until the drag is genuinely idle.
      if (sidebarMaxRef && sidebarMaxRef.current < SIDEBAR_MAX_WIDTH) {
        if (releaseConstraintTimerRef.current) {
          clearTimeout(releaseConstraintTimerRef.current);
        }
        releaseConstraintTimerRef.current = setTimeout(() => {
          releaseConstraintTimerRef.current = null;
          if (sidebarMaxRef) sidebarMaxRef.current = SIDEBAR_MAX_WIDTH;
        }, 600);
      }
    }
  }, [setWidth, setSidebarWidth, sidebarWidth, sidebarWidthRef, sidebarRef, containerRef, sidebarMaxRef, hasPinnedTabs, edgeTabIds.length]);

  // Run on mount (handles over-wide saved widths) and on every window resize.
  useEffect(() => {
    clampPanelToViewport();
    window.addEventListener('resize', clampPanelToViewport);
    return () => window.removeEventListener('resize', clampPanelToViewport);
  }, [clampPanelToViewport]);

  // Also clamp the moment the pinned panel first appears in the DOM.
  useEffect(() => {
    if (hasPinnedTabs) clampPanelToViewport();
  }, [hasPinnedTabs, clampPanelToViewport]);

  // Re-clamp when the workspace layout changes. Only the transition from
  // "no input selected" → "input selected" materially increases the workspace's
  // min-content width — going from a thin placeholder to a full four-column
  // attribute form + tasks table. The reverse transition (input → paddock)
  // contracts the workspace, so the panel is already at or below the correct
  // clamped size and no measurement is needed.
  //
  // Crucially, the rAF fires only ~16 ms after the Redux state change. At that
  // moment the workspace component may still be mid-transition (its own
  // useEffects, which run after paint, haven't settled yet). If we ran the DOM
  // overflow guard during the input→paddock transition, the guard would see the
  // workspace still wide (from the previous render) and spuriously shrink the
  // panel — causing the "shrinks once per toggle" progressive-shrinkage bug.
  //
  // hasPinnedTabs is intentionally omitted from deps: this effect reacts to
  // selection changes only. The hasPinnedTabs effect above already handles
  // pin/unpin events. Including hasPinnedTabs here would fire clamp immediately
  // after "pin all", which can trigger collapseAllRightPanelTabs and undo it.
  // panelRef.current is read inside the rAF (not captured at effect time) so
  // it always reflects whether a pinned panel actually exists at that moment.
  useEffect(() => {
    // Only clamp when an input is being selected (workspace expanding).
    // When deselecting (input → paddock), the panel is already at the correct
    // clamped size; running the DOM guard here would shrink it further based
    // on a still-settling workspace layout.
    if (!hasSelectedInput) return;

    // First pass: rAF fires before the browser paints, after React's DOM commit
    // and useLayoutEffect hooks. Handles most cases immediately.
    const rafId = requestAnimationFrame(() => {
      if (!panelRef.current) return;
      clampPanelToViewport();
    });
    // Second pass: some workspace components (e.g. the input document editor)
    // use useEffect (not useLayoutEffect) to measure and adjust their own
    // dimensions. Those effects run after paint, so the rAF above fires before
    // the workspace has reached its final layout. The 100 ms timeout fires well
    // after any post-paint effects, catching the case where paddock (no data)
    // switches to input (has data) and the workspace's min-content width only
    // stabilises after the first frame.
    const timerId = setTimeout(() => {
      if (!panelRef.current) return;
      clampPanelToViewport();
    }, 100);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timerId);
    };
  }, [clampPanelToViewport, hasSelectedInput]);

  // Clamp right panel in real time while the left sidebar is being dragged.
  // ResizeObserver fires on every layout change to the sidebar element —
  // unlike the window-resize listener, this covers in-drag size updates.
  useEffect(() => {
    const el = sidebarRef?.current;
    if (!el) return;
    const observer = new ResizeObserver(() => clampPanelToViewport());
    observer.observe(el);
    return () => observer.disconnect();
  }, [clampPanelToViewport, sidebarRef]);

  // Cancel pending timers and animation frames on unmount so they cannot
  // fire on a stale component instance.
  useEffect(() => {
    return () => {
      if (releaseConstraintTimerRef.current) {
        clearTimeout(releaseConstraintTimerRef.current);
      }
      if (sidebarCorrectionRafRef.current !== null) {
        cancelAnimationFrame(sidebarCorrectionRafRef.current);
      }
    };
  }, []);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handlePin = useCallback(
    (tabId: RightPanelTabId) => {
      dispatch(pinRightPanelTab(tabId));
    },
    [dispatch]
  );

  const handleClose = useCallback(
    (tabId: RightPanelTabId) => {
      dispatch(collapseRightPanelTab(tabId));
    },
    [dispatch]
  );

  const handleEdgeLabelClick = useCallback(
    (tabId: RightPanelTabId) => {
      if (tabs[tabId] === 'floating') {
        dispatch(pinRightPanelTab(tabId));
      } else {
        dispatch(floatRightPanelTab(tabId));
      }
    },
    [tabs, dispatch]
  );

  const handleMergeToggle = useCallback(() => {
    dispatch(setRightPanelMerged(!isMerged));
  }, [dispatch, isMerged]);

  const handlePinAll = useCallback(() => {
    dispatch(pinAllRightPanelTabs());
  }, [dispatch]);

  const handleUnpinAll = useCallback(() => {
    dispatch(collapseAllRightPanelTabs());
  }, [dispatch]);

  const handleResetAll = useCallback(() => {
    dispatch(resetAllRightPanelTabs());
    dispatch(setSidebarCollapsed(false));
    dispatch(resetSidebarWidth());
    dispatch(setSidebarInfoBannerVisible(true));
    dispatch(setSummaryHintVisible(true));
    localStorage.removeItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
    localStorage.removeItem(SIDEBAR_WIDTH_STORAGE_KEY);
    localStorage.removeItem(SIDEBAR_INFO_BANNER_STORAGE_KEY);
    localStorage.removeItem(SUMMARY_SCOPE_HINT_STORAGE_KEY);
  }, [dispatch]);

  const handleOpenLimeMate = useCallback(() => {
    dispatch(setRightPanelActiveTool(RIGHT_PANEL_TOOL_LIME_MATE));
  }, [dispatch]);

  const handleFloatingGeometryChange = useCallback(
    (tabId: RightPanelTabId, geometry: FloatingGeometry) => {
      dispatch(setRightPanelFloatingGeometry({ tabId, geometry }));
    },
    [dispatch]
  );

  const handleBringToFront = useCallback(
    (tabId: RightPanelTabId) => {
      dispatch(bringRightPanelTabToFront(tabId));
    },
    [dispatch]
  );

  // The Nutrition lens has its own charts panel on the right (OP-2508), so
  // Live totals — collapsed in Insights anyway — would only add a greyed-out
  // strip beside it. Hidden there; the panel's state is untouched, so it
  // comes back as it was on any other lens or tab.
  if (
    activeTab === INPUT_DOC_TAB_INSIGHTS &&
    activeLens === INPUT_DOC_LENS_NUTRITION
  ) {
    return null;
  }

  return (
    <>
      {/* ── Drag handle + pinned panel ─────────────────────────────────── */}
      {hasPinnedTabs && (
        <>
          <div
            onMouseDown={onMouseDown}
            onDoubleClick={() => setWidth(RIGHT_PANEL_INITIAL_WIDTH)}
            role="separator"
            aria-orientation="vertical"
            tabIndex={0}
            className="group relative z-20 flex w-1 flex-shrink-0 cursor-col-resize items-center justify-center bg-transparent transition-all hover:w-1.5 hover:bg-gray-300"
          >
            <ResizeHandle />
            {/* Collapse button — top third, visible on hover only. The icon
                grows when the surrounding resize-handle line is hovered
                (`group-hover`, from the outer div above), not on its own
                hover — the button is small enough that requiring a hover
                directly on it defeats the point of making it easier to hit. */}
            <div className="pointer-events-none absolute inset-x-0 top-0 flex h-1/3 items-center justify-center opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100">
              <Tooltip title="Collapse right panel" placement="left">
                <button
                  type="button"
                  aria-label="Collapse right panel"
                  onMouseDown={e => e.stopPropagation()}
                  onClick={handleUnpinAll}
                  className="-rotate-90 text-gray-dark transition-opacity hover:opacity-75"
                >
                  <IoIosArrowDropdownCircle
                    size={18}
                    className="transition-transform duration-150 group-hover:scale-[2]"
                  />
                </button>
              </Tooltip>
            </div>
          </div>

          <RightPinnedPanel
            ref={panelRef}
            width={width}
            tabs={tabs}
            tabOrder={tabOrder}
            pinnedTabIds={pinnedTabIds}
            activePinnedTabId={activePinnedTabId}
            isMerged={isMerged}
            mergedSectionExpanded={mergedSectionExpanded}
            onUnpin={handleClose}
            onUnpinAll={handleUnpinAll}
            onMergeToggle={handleMergeToggle}
          />
        </>
      )}

      {/* ── Edge labels ────────────────────────────────────────────────── */}
      {/* Phantom placeholder: reserves the edge-strip width (w-6 = 1.5rem) in
          the flex flow so the pinned panel cannot expand into that space.
          RightEdgeLabels is position:absolute so overflow:hidden on the
          FarmsPage container never clips it — but without this placeholder the
          panel would visually overlap the edge strip. Only rendered when the
          edge strip itself is visible (some tabs are not pinned). */}
      {edgeTabIds.length > 0 && (
        <div className="w-6 flex-shrink-0" aria-hidden="true" />
      )}
      <RightEdgeLabels
        tabIds={edgeTabIds}
        floatingTabIds={floatingTabIds}
        onTabClick={handleEdgeLabelClick}
        onTabDoubleClick={handlePin}
        onPinAll={handlePinAll}
        onUnpinAll={handleUnpinAll}
        onResetAll={handleResetAll}
        // Only needed while the pinned panel is closed — once it's open, the
        // header's tool selector is the way in.
        onOpenLimeMate={hasPinnedTabs ? undefined : handleOpenLimeMate}
        disabledMessage={
          isScopeApplicable ? undefined : 'Select a paddock or plan to view'
        }
      />

      {/* ── Floating panels (one per floating tab) ─────────────────────── */}
      {/* Not rendered at org/farm scope — the panel is inert there, so any
          previously-floating tab stays hidden until scope is applicable
          again (its geometry and status are untouched, not lost). */}
      {isScopeApplicable && floatingTabIds.map((tabId, idx) => {
        const labelIndex = edgeTabIds.indexOf(tabId);
        const isTopmost = idx === floatingTabIds.length - 1;
        return (
          /* pointer-events-none wrapper makes Rnd viewport-relative
             without blocking clicks in the transparent regions */
          <div key={tabId} className="pointer-events-none fixed inset-0 z-50">
            <RightFloatingPanel
              tabId={tabId}
              labelIndex={labelIndex >= 0 ? labelIndex : 0}
              savedGeometry={floatingGeometry[tabId]}
              isTopmost={isTopmost}
              onGeometryChange={(geometry) =>
                handleFloatingGeometryChange(tabId, geometry)
              }
              onBringToFront={() => handleBringToFront(tabId)}
              onPin={() => handlePin(tabId)}
              onClose={() => handleClose(tabId)}
            />
          </div>
        );
      })}
    </>
  );
};

export default RightPanelShell;
